import { useEffect, useState } from 'react';
import { DollarSign, FileText, StickyNote, Users } from 'lucide-react';
import { SecaoForm } from '../../components/SecaoForm';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { MoneyInput } from '../../components/inputs/MoneyInput';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatMoeda } from '@fiodecobre/shared';

interface Categoria { id: number; nome: string }
interface Fornecedor { id: number; razao_social: string; nome_fantasia: string | null; observacoes?: string | null }

interface Props {
  aberto: boolean;
  conta?: any;
  categorias: Categoria[];
  fornecedores: Fornecedor[];
  onFechar: () => void;
  onSalvo: () => void;
}

const PARCELAS_RAPIDAS = [1, 2, 3, 6, 12];

// Altura padrao dos campos do modal — bate com .combo-input (padding 8px + font 14px).
// Aplicada via style inline pra padronizar input/select/MoneyInput visualmente.
const ALT_INPUT = 38;

function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// Avanca N meses mantendo o DIA cravado (10/06 -> 10/07 -> 10/08...).
// Dia que nao existe no mes alvo (ex: 31 em fevereiro) cai pro ultimo dia do mes.
function addMonths(dateStr: string, months: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const alvo = (m! - 1) + months;
  const ano = y! + Math.floor(alvo / 12);
  const mes = ((alvo % 12) + 12) % 12;
  const ultimoDia = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  const dia = Math.min(d!, ultimoDia);
  return new Date(Date.UTC(ano, mes, dia)).toISOString().slice(0, 10);
}

function ObsFornecedor({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--input-fg)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      <strong>Obs. do fornecedor:</strong> {texto}
    </div>
  );
}

export function ModalContaPagar({ aberto, conta, categorias, fornecedores, onFechar, onSalvo }: Props) {
  const toast = useToast();
  const editando = !!conta;
  const [salvando, setSalvando] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState(false);
  const [novoFornecedor, setNovoFornecedor] = useState(false);

  // A lista do pai e lida uma vez, na montagem da tela: fornecedor cadastrado depois (noutra aba)
  // nao aparecia no combo e o titulo saia sem fornecedor, calado. Reler ao abrir o modal.
  const [listaFornecedores, setListaFornecedores] = useState<Fornecedor[]>(fornecedores);
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    api.get<Fornecedor[]>('/api/fornecedores', { query: { todos: true } })
      .then((l) => { if (vivo) setListaFornecedores(l); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [aberto]);

  const [form, setForm] = useState({
    numero_documento: conta?.numero_documento || '',
    numero_nf: conta?.numero_nf || '',
    categoria_despesa_id: conta?.categoria_despesa_id ? String(conta.categoria_despesa_id) : '',
    fornecedor_id: conta?.fornecedor_id ? String(conta.fornecedor_id) : '',
    fornecedor_produto_id: conta?.fornecedor_produto_id ? String(conta.fornecedor_produto_id) : '',
    descricao: conta?.descricao || '',
    valor: conta ? Number(conta.valor) : 0,
    data_emissao: conta?.data_emissao?.slice(0, 10) || hojeISO(),
    primeiro_vencimento: conta?.data_vencimento?.slice(0, 10) || hojeISO(),
    parcelas: 1,
    intervalo_meses: 1,
    observacoes: conta?.observacoes || '',
  });

  // Pra editar, o vencimento e direto (nao parcelas)
  const [dataVencimento, setDataVencimento] = useState(conta?.data_vencimento?.slice(0, 10) || hojeISO());

  async function salvar() {
    if (!form.categoria_despesa_id) { toast.erro('Selecione a categoria'); return; }
    if (!form.descricao.trim()) { toast.erro('Informe a descricao'); return; }
    if (form.valor <= 0) { toast.erro('Valor invalido'); return; }

    setSalvando(true);
    try {
      if (editando) {
        await api.put(`/api/contas-pagar/${conta.id}`, {
          numero_documento: form.numero_documento || null,
          numero_nf: form.numero_nf.trim() || null,
          categoria_despesa_id: Number(form.categoria_despesa_id),
          fornecedor_id: form.fornecedor_id ? Number(form.fornecedor_id) : null,
          fornecedor_produto_id: form.fornecedor_produto_id ? Number(form.fornecedor_produto_id) : null,
          descricao: form.descricao.trim(),
          valor: form.valor,
          data_emissao: form.data_emissao,
          data_vencimento: dataVencimento,
          observacoes: form.observacoes || null,
        });
        toast.sucesso('Conta atualizada');
      } else {
        await api.post('/api/contas-pagar', {
          numero_documento: form.numero_documento || undefined,
          numero_nf: form.numero_nf.trim() || undefined,
          categoria_despesa_id: Number(form.categoria_despesa_id),
          fornecedor_id: form.fornecedor_id ? Number(form.fornecedor_id) : undefined,
          fornecedor_produto_id: form.fornecedor_produto_id ? Number(form.fornecedor_produto_id) : undefined,
          descricao: form.descricao.trim(),
          valor_total: form.valor,
          parcelas: form.parcelas,
          data_emissao: form.data_emissao,
          primeiro_vencimento: form.primeiro_vencimento,
          intervalo_meses: form.intervalo_meses,
          observacoes: form.observacoes || undefined,
        });
        toast.sucesso(form.parcelas > 1 ? `${form.parcelas} parcelas criadas` : 'Conta criada');
      }
      onSalvo();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  // Observacoes do CADASTRO do fornecedor escolhido (so leitura — edita-se em Fornecedores).
  function obsDe(id: string): string | null {
    if (!id) return null;
    const f = listaFornecedores.find((x) => String(x.id) === id);
    return f?.observacoes?.trim() || null;
  }

  // Recorrente: cada titulo carrega o valor digitado; o total e a soma das parcelas.
  const valorTotalGerado = form.valor * form.parcelas;

  return (
    <>
      <Modal
        aberto={aberto}
        onFechar={onFechar}
        titulo={editando ? `Editar conta — ${conta?.numero_documento || conta?.id}` : 'Nova conta a pagar'}
        largura={960}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
            <button type="button" className="btn-primario" disabled={salvando} onClick={() => void salvar()}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </>
        }
      >
        {/* Mesmo esqueleto do cadastro de Fornecedores (pedido dele, 02/10/2026): .form-grid-12 + SecaoForm. */}
        <div className="form-grid form-grid-12">
          <SecaoForm icone={FileText} titulo="Identificação" primeira />
          <label className="col-span-12 md:col-span-4">
            Numero do documento (opcional)
            <input
              type="text"
              value={form.numero_documento}
              onChange={(e) => setForm({ ...form, numero_documento: upperMantendoCursor(e) })}
              placeholder="Auto: CP-XXXXXX"
            />
          </label>
          <div className="col-span-12 md:col-span-8 form-grid-campo">
            <label>Categoria de despesa *</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
              <div style={{ flex: 1 }}>
                <ComboBox
                  opcoes={categorias.map((c) => ({ value: c.id, label: c.nome }))}
                  value={form.categoria_despesa_id}
                  onChange={(v) => setForm({ ...form, categoria_despesa_id: String(v) })}
                  placeholder="Selecione..."
                />
              </div>
              <button type="button" className="btn-icon" title="Nova categoria" onClick={() => setNovaCategoria(true)}>+</button>
            </div>
          </div>
          <label className="col-span-12">
            Descricao *
            <input
              type="text"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: upperMantendoCursor(e) })}
              placeholder="Ex: ALUGUEL MAIO/26 / FORNECEDOR XPTO NF 12345"
              autoFocus={!editando}
            />
          </label>

          <SecaoForm icone={Users} titulo="Fornecedores" />
          <div className="col-span-12 md:col-span-6 form-grid-campo">
            <label title="Quem recebe o pagamento (ex: transportadora EXECUTIVA EXPRESS)">Fornecedor do Titulo (opcional)</label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
              <div style={{ flex: 1 }}>
                <ComboBox
                  opcoes={[{ value: '', label: '— sem fornecedor —' }, ...listaFornecedores.map((f) => ({ value: f.id, label: f.nome_fantasia || f.razao_social }))]}
                  value={form.fornecedor_id}
                  onChange={(v) => setForm({ ...form, fornecedor_id: String(v) })}
                  placeholder="Sem fornecedor"
                />
              </div>
              <button type="button" className="btn-icon" title="Novo fornecedor" onClick={() => setNovoFornecedor(true)}>+</button>
            </div>
            <ObsFornecedor texto={obsDe(form.fornecedor_id)} />
          </div>
          <div className="col-span-12 md:col-span-6 form-grid-campo">
            <label title="Quem originou o gasto (ex: BETEL nos fretes da EXECUTIVA EXPRESS). Util pra rastrear custo logistico por fornecedor real.">
              Fornecedor do Produto (opcional)
            </label>
            <ComboBox
              opcoes={[{ value: '', label: '— nao se aplica —' }, ...listaFornecedores.map((f) => ({ value: f.id, label: f.nome_fantasia || f.razao_social }))]}
              value={form.fornecedor_produto_id}
              onChange={(v) => setForm({ ...form, fornecedor_produto_id: String(v) })}
              placeholder="Nao se aplica"
            />
            <ObsFornecedor texto={obsDe(form.fornecedor_produto_id)} />
          </div>

          <SecaoForm icone={DollarSign} titulo="Valores e datas" />
          <label className="col-span-6 md:col-span-3">
            Numero NF
            <input
              type="text"
              value={form.numero_nf}
              onChange={(e) => setForm({ ...form, numero_nf: e.target.value.replace(/\D/g, '') })}
              placeholder="Ex: 25809196"
              style={{ textAlign: 'center' }}
            />
          </label>
          <label className="col-span-6 md:col-span-3">
            {editando ? 'Valor *' : 'Valor de cada titulo *'}
            <MoneyInput className="cel-num" valor={form.valor} onChangeValor={(n) => setForm({ ...form, valor: n })} style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }} />
          </label>
          <label className="col-span-6 md:col-span-3">
            Data de emissao
            <input type="date" value={form.data_emissao} onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} style={{ textAlign: 'center' }} />
          </label>
          {editando ? (
            <label className="col-span-6 md:col-span-3">
              Vencimento *
              <input type="date" value={dataVencimento} onChange={(e) => setDataVencimento(e.target.value)} style={{ textAlign: 'center' }} />
            </label>
          ) : (
            <>
              <label className="col-span-6 md:col-span-3">
                Primeiro vencimento *
                <input type="date" value={form.primeiro_vencimento} onChange={(e) => setForm({ ...form, primeiro_vencimento: e.target.value })} style={{ textAlign: 'center' }} />
              </label>
              <div className="col-span-12 md:col-span-6 form-grid-campo">
                <label>Parcelas</label>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: ALT_INPUT }}>
                  {PARCELAS_RAPIDAS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`aba-btn ${form.parcelas === n ? 'aba-btn-ativo' : ''}`}
                      style={{ padding: '4px 10px', fontSize: 13 }}
                      onClick={() => setForm({ ...form, parcelas: n })}
                    >
                      {n}x
                    </button>
                  ))}
                  <input
                    type="number"
                    className="cel-num"
                    min={1}
                    value={form.parcelas}
                    onChange={(e) => setForm({ ...form, parcelas: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                    style={{ width: 60, height: ALT_INPUT, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              {form.parcelas > 1 && (
                <>
                  <label className="col-span-6 md:col-span-3">
                    Intervalo (meses)
                    <input
                      type="number"
                      className="cel-num"
                      min={1}
                      value={form.intervalo_meses}
                      onChange={(e) => setForm({ ...form, intervalo_meses: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                    />
                  </label>
                  <div className="col-span-6 md:col-span-3" style={{ alignSelf: 'end', fontSize: 13, color: 'var(--input-fg)', paddingBottom: 8 }}>
                    <span className="cel-num">{form.parcelas}x</span> de <strong className="cel-num">{formatMoeda(form.valor)}</strong> · total <strong className="cel-num">{formatMoeda(valorTotalGerado)}</strong>
                  </div>
                  {form.primeiro_vencimento && (
                    <div className="col-span-12" style={{ fontSize: 12, color: 'var(--input-fg)', marginTop: -4 }}>
                      <strong>Parcelas:</strong>{' '}
                      {Array.from({ length: form.parcelas }, (_, i) => addMonths(form.primeiro_vencimento, i * form.intervalo_meses)).join(' · ')}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <SecaoForm icone={StickyNote} titulo="Observações" />
          <label className="col-span-12">
            <textarea
              rows={2}
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
            />
          </label>
        </div>
      </Modal>

      {novaCategoria && (
        <ModalCadastroRapido
          titulo="Nova categoria de despesa"
          endpoint="/api/categorias-despesa"
          campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
          onSalvo={(c: any) => {
            // adiciona localmente (carregar de novo via parent)
            categorias.push({ id: c.id, nome: c.nome });
            setForm((f) => ({ ...f, categoria_despesa_id: String(c.id) }));
            setNovaCategoria(false);
          }}
          onFechar={() => setNovaCategoria(false)}
        />
      )}

      {novoFornecedor && (
        <ModalCadastroRapido
          titulo="Novo fornecedor"
          endpoint="/api/fornecedores"
          campos={[
            { key: 'nome_fantasia', label: 'Nome', obrigatorio: true },
            { key: 'tipo_pessoa', label: 'Tipo', tipo: 'select', obrigatorio: true, valor: 'PJ', opcoes: [{ value: 'PJ', label: 'Pessoa juridica' }, { value: 'PF', label: 'Pessoa fisica' }] },
          ]}
          onSalvo={(f: any) => {
            setListaFornecedores((l) => [...l, { id: f.id, razao_social: f.razao_social ?? '', nome_fantasia: f.nome_fantasia }]);
            setForm((x) => ({ ...x, fornecedor_id: String(f.id) }));
            setNovoFornecedor(false);
          }}
          onFechar={() => setNovoFornecedor(false)}
        />
      )}
    </>
  );
}
